import React, { useState } from 'react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { MaintenanceRequestModal } from '../../components/modals/MaintenanceRequestModal';
import { RequestCard } from '../../components/maintenance/RequestCard';
import { MOCK_MAINTENANCE_REQUESTS } from '../../utils/mockData';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Wrench, Search } from 'lucide-react';
import { EmptyState } from '../../components/shared/EmptyState';
import { useToast } from '../../hooks/useToast';
export function MaintenanceRequests() {
  const {
    toast
  } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [requests] = useState(MOCK_MAINTENANCE_REQUESTS);
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'in_progress' | 'completed'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const handleSuccess = () => {
    toast.success('Maintenance request submitted successfully!');
    setIsModalOpen(false);
  };
  const filteredRequests = requests.filter(req => {
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    const matchesSearch = req.title.toLowerCase().includes(searchQuery.toLowerCase()) || req.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors pb-16 lg:pb-0" data-id="element-4704">
      <Sidebar data-id="element-4705" />

      <main className="flex-1 lg:ml-[240px] transition-all duration-300" data-id="element-4706">
        <DashboardHeader data-id="element-4707" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-4708">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-4709">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-8 gap-4" data-id="element-4710">
              <div className="min-w-0" data-id="element-4711">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate" data-id="element-4712">
                  Maintenance Requests
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-4713">
                  Report and track issues with your property.
                </p>
              </div>
              <button onClick={() => setIsModalOpen(true)} className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-4714">
                <Plus className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-4715" />
                New Request
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6 lg:mb-8 transition-colors" data-id="element-4716">
              <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center" data-id="element-4717">
                <div className="relative flex-1" data-id="element-4718">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5 flex-shrink-0" data-id="element-4719" />
                  <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search requests..." className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors" data-id="element-4720" />
                </div>

                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors whitespace-nowrap" data-id="element-4721">
                  <option value="all" data-id="element-4722">All Status</option>
                  <option value="submitted" data-id="element-4723">Submitted</option>
                  <option value="in_progress" data-id="element-4724">In Progress</option>
                  <option value="completed" data-id="element-4725">Completed</option>
                </select>
              </div>
            </div>

            <div className="mb-4" data-id="element-4726">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4727">
                Showing{' '}
                <span className="font-bold text-gray-900 dark:text-white" data-id="element-4728">
                  {filteredRequests.length}
                </span>{' '}
                request{filteredRequests.length !== 1 ? 's' : ''}
              </p>
            </div>

            <AnimatePresence mode="wait" data-id="element-4729">
              {filteredRequests.length > 0 ? <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6" data-id="element-4730">
                  {filteredRequests.map((req, idx) => <motion.div key={req.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                delay: idx * 0.05
              }} data-id="element-4731">
                      <RequestCard request={req} onClick={() => {}} data-id="element-4732" />
                    </motion.div>)}
                </motion.div> : <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} data-id="element-4733">
                  <EmptyState icon={Wrench} title={searchQuery || statusFilter !== 'all' ? 'No requests found' : 'No maintenance requests'} description={searchQuery || statusFilter !== 'all' ? 'Try adjusting your filters or search query' : "You haven't submitted any maintenance requests yet."} action={{
                label: 'Submit Request',
                onClick: () => setIsModalOpen(true)
              }} data-id="element-4734" />
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>
      </main>

      <MaintenanceRequestModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSuccess={handleSuccess} data-id="element-4735" />
    </div>;
}